import { HairlineCard, Stack, StatusChip, Typography, type StatusKind } from '@munifocus/ui';
import { useTranslation } from 'react-i18next';
import type { WorkflowDeadlineState } from '@munifocus/contracts-gov';
import { pickLang } from '../../lib/bylaw-workflows-api';

function tone(d: WorkflowDeadlineState): StatusKind {
  if (d.expired) return 'emergency';
  if (d.daysRemaining <= 3) return 'warning';
  return 'pending';
}

/**
 * The file's live statutory clocks, as its definition describes them. Each
 * names the statute it runs under — or says plainly that nobody has configured
 * the citation, rather than printing an empty space a reader could miss.
 */
export function WorkflowDeadlinesPanel({ deadlines }: { deadlines: WorkflowDeadlineState[] }) {
  const { t, i18n } = useTranslation();

  return (
    <HairlineCard sx={{ p: 2.5 }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        {t('gov.bylawWorkflow.deadlines.title')}
      </Typography>
      {deadlines.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {t('gov.bylawWorkflow.deadlines.empty')}
        </Typography>
      ) : (
        <Stack spacing={1.25}>
          {deadlines.map((d) => {
            let due: string;
            if (d.expired) {
              due = t('gov.bylawWorkflow.deadlines.overdue', { count: -d.daysRemaining });
            } else if (d.daysRemaining === 0) {
              due = t('gov.bylawWorkflow.deadlines.dueToday');
            } else {
              due = t('gov.bylawWorkflow.deadlines.daysLeft', { count: d.daysRemaining });
            }
            return (
              <Stack
                key={d.key}
                direction="row"
                spacing={2}
                sx={{ justifyContent: 'space-between', alignItems: 'center' }}
              >
                <Stack spacing={0.25} sx={{ minWidth: 0 }}>
                  <Typography variant="body2">{pickLang(d.label, i18n.language)}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {d.statuteRef || t('gov.bylawWorkflow.citationNotConfigured')} ·{' '}
                    {t('gov.bylawWorkflow.deadlines.due', { date: d.deadlineIso })}
                  </Typography>
                </Stack>
                <StatusChip status={tone(d)} label={due} />
              </Stack>
            );
          })}
        </Stack>
      )}
    </HairlineCard>
  );
}
